/**
 * DashboardSidebar Component
 * Side navigation with menu items and value-added services
 * Mobile-responsive with overlay mode on small screens
 * Supports freemium plan restrictions with lock icons
 */
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaIdCard, FaExternalLinkAlt, FaAddressBook, FaWhatsapp, FaLock } from 'react-icons/fa';
import ValueAddedServices from './ValueAddedServices';
import UpgradeModal from '../UpgradeModal';
import { ModeBadge } from '../shared';

const DIGILOCKER_URL = "https://accounts.digilocker.gov.in/v3/cbdbc9840251d5aff0c3c84356a1f235e8bab67d4a09858bb105cb1ec77eaf19--en";

const CRM_URL = "https://crm.myviralbrand.com/signin";
const WHATSAPP_MARKETING_URL = "https://www.edigistore.in";

const DashboardSidebar = ({ menuItems, userRole, isOpen, onClose, userPlan }) => {
  const location = useLocation();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const isFreePlan = userPlan === 'plus';

  const handleDigiLockerClick = () => {
    window.open(DIGILOCKER_URL, '_blank', 'noopener,noreferrer');
  };

  const handleLockedClick = (e) => {
    e.preventDefault();
    setShowUpgradeModal(true);
  };

  return (
    <>
      {/* Mobile Overlay Background */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <aside className={`
        fixed lg:relative inset-y-0 left-0 z-50 lg:z-auto
        w-64 bg-gradient-to-b from-[#1A2942] to-[#0A1929] border-r border-[#2D5A8F] p-4
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0 lg:hidden'}
        overflow-y-auto
      `}>
        {/* Mobile Close Button */}
        <button 
          onClick={onClose}
          className="lg:hidden absolute top-4 right-4 text-gray-400 hover:text-white"
        >
          ✕
        </button>

        {/* Plan Badge */}
        {userRole === 'client' && (
          <div className={`mb-4 mt-8 lg:mt-0 px-3 py-2 rounded-lg text-center text-xs font-bold tracking-wider ${
            isFreePlan 
              ? 'bg-gray-700/50 border border-gray-600 text-gray-300' 
              : 'bg-[#FFD700]/10 border border-[#FFD700]/30 text-[#FFD700]'
          }`} data-testid="plan-badge">
            {isFreePlan ? 'PLUS (FREE)' : 'PRO+ (PREMIUM)'}
          </div>
        )}

        {/* DIY vs Guided legend — helps users decode the badges on each tool */}
        {userRole === 'client' && (
          <div className="mb-3 px-2 flex items-center justify-center gap-3 text-[10px]" data-testid="mode-legend">
            <span className="inline-flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-green-400"></span>
              <span className="text-gray-400">DIY</span>
            </span>
            <span className="text-gray-600">·</span>
            <span className="inline-flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-purple-400"></span>
              <span className="text-gray-400">Guided</span>
            </span>
          </div>
        )}
        
        {/* Navigation Menu */}
        <nav className={`space-y-2 ${userRole !== 'client' ? 'mt-8 lg:mt-0' : ''}`}>
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            const uniqueKey = `${item.path}-${item.label}`;
            const isLocked = isFreePlan && userRole === 'client' && !item.freeAccess;
            
            // Handle Coming Soon items
            if (item.comingSoon) {
              return (
                <div
                  key={uniqueKey}
                  className="flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg bg-[#1E3A5F]/50 border border-dashed border-[#FFD700]/50 cursor-not-allowed"
                  data-testid={`menu-item-${item.label.toLowerCase().replace(/\s+/g, '-')}-soon`}
                >
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    <Icon size={18} className="text-[#FFD700]" />
                    <span className="text-gray-400 text-sm">{item.label}</span>
                  </div>
                  <span className="px-1.5 py-0.5 bg-[#FFD700]/20 text-[#FFD700] text-[10px] font-bold rounded-full">
                    SOON
                  </span>
                </div>
              );
            }

            // Handle Locked items for free plan
            if (isLocked) {
              return (
                <button
                  key={uniqueKey}
                  onClick={handleLockedClick}
                  className="w-full flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg bg-[#1E3A5F]/30 text-gray-500 hover:bg-[#1E3A5F]/50 hover:text-gray-400 transition-all group"
                  data-testid={`menu-item-${item.label.toLowerCase().replace(/\s+/g, '-')}-locked`}
                >
                  <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
                    <Icon size={18} className="opacity-50 flex-shrink-0" />
                    <span className="text-sm opacity-70 truncate">{item.label}</span>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {item.mode && <ModeBadge mode={item.mode} size="xs" />}
                    <FaLock size={12} className="text-gray-600 group-hover:text-[#FFD700] transition-colors" />
                  </div>
                </button>
              );
            }
            
            return (
              <Link
                key={uniqueKey}
                to={item.path}
                onClick={onClose}
                className={`flex items-center justify-between space-x-2 sm:space-x-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg transition-all ${
                  isActive 
                    ? 'bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold shadow-lg' 
                    : 'text-gray-300 hover:bg-[#1E3A5F] hover:text-white'
                }`}
                data-testid={`menu-item-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
                  <Icon size={18} className="flex-shrink-0" />
                  <span className="text-sm sm:text-base truncate">{item.label}</span>
                </div>
                {item.mode && <ModeBadge mode={item.mode} size="xs" />}
              </Link>
            );
          })}
        </nav>

        {/* CRM Link - Advisor & Manager */}
        {(userRole === 'advisor' || userRole === 'manager') && (
          <div className="mt-4 pt-4 border-t border-[#2D5A8F] space-y-3">
            <button
              onClick={() => window.open(CRM_URL, '_blank', 'noopener,noreferrer')}
              className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl bg-gradient-to-r from-[#1E3A5F] to-[#2D5A8F] border-2 border-[#00BFFF] hover:border-[#00E5FF] hover:shadow-lg hover:shadow-[#00BFFF]/20 transition-all group"
              data-testid="crm-btn"
            >
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#00BFFF] to-[#0088CC] flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
                <FaAddressBook size={20} className="text-white" />
              </div>
              <div className="text-left flex-1">
                <p className="text-white font-bold text-sm">CRM</p>
                <p className="text-gray-400 text-xs">Manage your leads</p>
              </div>
              <FaExternalLinkAlt size={12} className="text-[#00BFFF] opacity-70 group-hover:opacity-100" />
            </button>
            <button
              onClick={() => window.open(WHATSAPP_MARKETING_URL, '_blank', 'noopener,noreferrer')}
              className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl bg-gradient-to-r from-[#1E3A5F] to-[#2D5A8F] border-2 border-[#25D366] hover:border-[#2EE676] hover:shadow-lg hover:shadow-[#25D366]/20 transition-all group"
              data-testid="whatsapp-marketing-btn"
            >
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#25D366] to-[#128C7E] flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
                <FaWhatsapp size={22} className="text-white" />
              </div>
              <div className="text-left flex-1">
                <p className="text-white font-bold text-sm">WhatsApp Marketing</p>
                <p className="text-gray-400 text-xs">Grow your business</p>
              </div>
              <FaExternalLinkAlt size={12} className="text-[#25D366] opacity-70 group-hover:opacity-100" />
            </button>
          </div>
        )}

        {/* DigiLocker Link - Client, Advisor, Manager */}
        {(userRole === 'client' || userRole === 'advisor' || userRole === 'manager') && (
          <div className="mt-4 pt-4 border-t border-[#2D5A8F]">
            <button
              onClick={handleDigiLockerClick}
              className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl bg-gradient-to-r from-[#1E3A5F] to-[#2D5A8F] border-2 border-[#FFD700] hover:border-[#FFA500] hover:shadow-lg hover:shadow-[#FFD700]/20 transition-all group"
              data-testid="digilocker-btn"
            >
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#FFD700] to-[#FFA500] flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
                <FaIdCard size={20} className="text-[#0A1929]" />
              </div>
              <div className="text-left flex-1">
                <p className="text-white font-bold text-sm">DigiLocker</p>
                <p className="text-gray-400 text-xs">Access your documents</p>
              </div>
              <FaExternalLinkAlt size={12} className="text-[#FFD700] opacity-70 group-hover:opacity-100" />
            </button>
          </div>
        )}

        {/* Upgrade CTA for Free Users */}
        {isFreePlan && userRole === 'client' && (
          <div className="mt-4 pt-4 border-t border-[#2D5A8F]">
            <button
              onClick={() => setShowUpgradeModal(true)}
              className="w-full py-3 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-xl text-sm hover:from-[#FFD700] hover:to-[#D4AF37] transition-all shadow-lg shadow-[#FFD700]/20"
              data-testid="sidebar-upgrade-btn"
            >
              Upgrade to PRO+
            </button>
          </div>
        )}

        {/* Value Added Services - Client Only */}
        {userRole === 'client' && <ValueAddedServices isFreePlan={isFreePlan} />}

        {/* Sidebar Footer - AI Feature */}
        <div className="mt-6 sm:mt-8 p-3 sm:p-4 bg-[#1E3A5F] rounded-lg border border-[#D4AF37]">
          <p className="text-[#FFD700] text-xs font-semibold mb-1">Arth Gurukul AI</p>
          <p className="text-gray-400 text-[10px] sm:text-xs">Get intelligent recommendations for every financial decision</p>
        </div>
      </aside>

      {/* Upgrade Modal */}
      <UpgradeModal isOpen={showUpgradeModal} onClose={() => setShowUpgradeModal(false)} />
    </>
  );
};

export default DashboardSidebar;
